import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, ArrowRight, X, Smartphone } from "lucide-react";

const STORAGE_KEY = "tc-announce-shiftbridge-v1";
const SHIFTBRIDGE_LINK = "https://shifts.tritalcare.com/";

/**
 * Subtle announcement strip above the utility bar.
 * Teases TRITAL ShiftBridge™ (PRN marketplace) + iOS/Android app launch.
 * Dismissable with 7-day localStorage suppression.
 */
export default function AnnouncementStrip() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) { setVisible(true); return; }
      const dismissedAt = parseInt(raw, 10);
      if (Number.isNaN(dismissedAt)) { setVisible(true); return; }
      const sevenDays = 7 * 24 * 60 * 60 * 1000;
      if (Date.now() - dismissedAt > sevenDays) setVisible(true);
    } catch { setVisible(true); }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  return (
    <AnimatePresence initial={false}>
      {visible && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.28, ease: [0.16, 0.84, 0.44, 1] }}
          className="relative overflow-hidden"
          data-testid="announcement-strip"
          role="region"
          aria-label="Platform announcement"
        >
          {/* Background — subtle navy → red sheen, consistent with brand accents */}
          <div
            className="relative"
            style={{
              background:
                "linear-gradient(92deg, #050B2E 0%, #091442 55%, #2A1036 82%, #3F0A0F 100%)",
            }}
          >
            {/* red top hairline */}
            <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-transparent via-trital-red/70 to-transparent" aria-hidden="true" />

            <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-9 sm:h-10 flex items-center gap-3">
              {/* Live dot + NEW pill */}
              <div className="hidden sm:flex items-center gap-2 shrink-0">
                <span className="relative flex items-center justify-center w-2 h-2">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-trital-red opacity-70 animate-pulse-red" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-trital-red" />
                </span>
                <span className="px-2 py-0.5 rounded-full bg-trital-red text-white text-[10px] font-bold tracking-[0.22em] uppercase">
                  New
                </span>
              </div>

              {/* Message */}
              <div className="flex-1 min-w-0 flex items-center gap-2 text-white/90 text-[12.5px] sm:text-[13px] truncate">
                <Sparkles className="w-3.5 h-3.5 text-trital-red shrink-0" strokeWidth={1.8} aria-hidden="true" />
                <span className="truncate">
                  <span className="font-display font-bold text-white">TRITAL ShiftBridge™</span>
                  <span className="hidden sm:inline text-white/60"> — PRN shift marketplace launching soon</span>
                  </span>
                  
                <Smartphone className="hidden lg:inline w-3.5 h-3.5 text-white/45 shrink-0" strokeWidth={1.8} aria-hidden="true" />
              </div>

              {/* CTA */}
              <a
                href={SHIFTBRIDGE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-1 shrink-0 text-[12.5px] sm:text-[13px] font-semibold text-white hover:text-trital-red transition-colors"
                data-testid="announcement-cta"
              >
                Explore ShiftBridge
                <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" strokeWidth={1.8} />
              </a>

              {/* Dismiss */}
              <button
                type="button"
                onClick={dismiss}
                aria-label="Dismiss announcement"
                className="shrink-0 p-1 rounded-full text-white/55 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="announcement-dismiss"
              >
                <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
